import {
  changeControlControllerGetRun,
  changeControlControllerListRunItems,
  changeControlControllerListRuns,
  changeControlControllerResumeRun,
  changeControlControllerStartBillOfOperationsRelease,
  changeControlControllerStartOperationRelease,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import type {
  ChangeControlRunItem,
  ChangeControlRunResponse,
  ChangeControlRunStatus,
  StartBillOfOperationsReleaseInput,
  StartOperationReleaseInput,
} from './types';
import { isTerminalStatus } from './types';

export const CHANGE_CONTROL_KEY = 'change_control' as const;

const POLL_INTERVAL_MS = 1500;

// ── List runs ─────────────────────────────────────────────────────────

export function changeControlRunsQueryOptions(
  partnerId: string | undefined,
  filters: { status?: ChangeControlRunStatus; limit?: number; offset?: number } = {},
) {
  return queryOptions({
    queryKey: [CHANGE_CONTROL_KEY, 'runs', partnerId, filters] as const,
    queryFn: async ({ signal }): Promise<ChangeControlRunResponse[]> => {
      const res = await changeControlControllerListRuns(
        {
          partner_id: partnerId ?? '',
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.limit !== undefined ? { limit: filters.limit } : {}),
          ...(filters.offset !== undefined ? { offset: filters.offset } : {}),
        },
        { signal },
      );
      return expectData<ChangeControlRunResponse[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 5_000,
    placeholderData: keepPreviousData,
  });
}

// ── Get one run (with progress polling) ───────────────────────────────

export function changeControlRunQueryOptions(runId: string | undefined) {
  return queryOptions({
    queryKey: [CHANGE_CONTROL_KEY, 'run', runId] as const,
    queryFn: async ({ signal }): Promise<ChangeControlRunResponse> => {
      const res = await changeControlControllerGetRun(runId ?? '', { signal });
      return expectData<ChangeControlRunResponse>(res);
    },
    enabled: !!runId,
    // Short staleTime keeps mounts from triggering an immediate refetch
    // while still letting the poll interval drive live updates.
    staleTime: 1_000,
    placeholderData: keepPreviousData,
    // Poll while the run is in flight; stop on terminal status.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return POLL_INTERVAL_MS;
      return isTerminalStatus(data.status) ? false : POLL_INTERVAL_MS;
    },
  });
}

// ── List run items ────────────────────────────────────────────────────

export function changeControlRunItemsQueryOptions(
  runId: string | undefined,
  filters: { status?: ChangeControlRunStatus } = {},
) {
  return queryOptions({
    queryKey: [CHANGE_CONTROL_KEY, 'run-items', runId, filters] as const,
    queryFn: async ({ signal }): Promise<ChangeControlRunItem[]> => {
      const res = await changeControlControllerListRunItems(
        runId ?? '',
        filters.status ? { status: filters.status } : undefined,
        { signal },
      );
      return expectData<ChangeControlRunItem[]>(res);
    },
    enabled: !!runId,
    staleTime: 5_000,
    placeholderData: keepPreviousData,
  });
}

// ── Start Bill of Operations release ──────────────────────────────────

export function useStartBillOfOperationsRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StartBillOfOperationsReleaseInput) => {
      const res = await changeControlControllerStartBillOfOperationsRelease(input);
      return expectData<ChangeControlRunResponse>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'runs'] });
      // Invalidate the matrix so it picks up the new
      // active_release_run_id and starts polling for live progress.
      void queryClient.invalidateQueries({ queryKey: ['bill_of_operations'] });
    },
  });
}

// ── Start operation release ───────────────────────────────────────────

export function useStartOperationRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: StartOperationReleaseInput) => {
      const res = await changeControlControllerStartOperationRelease(input);
      return expectData<ChangeControlRunResponse>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'runs'] });
    },
  });
}

// ── Resume a run ──────────────────────────────────────────────────────

export function useResumeRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (runId: string) => {
      // The endpoint responds 202 with no documented body in the spec, so the
      // generated function types `data` as `void`. The pre-existing contract
      // surfaced `{ requeued }` to the run-detail page; preserve it.
      const res = await changeControlControllerResumeRun(runId);
      return expectData<{ requeued: number }>(res);
    },
    onSuccess: (_data, runId) => {
      void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'run', runId] });
      void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'run-items', runId] });
    },
  });
}
